import { useCallback } from 'react';
import type { Fetcher } from 'react-router';

import {
  isAddToCartFetcherFor,
  useAddToCartFetchers,
} from '@jane/bloom/hooks/useAddToCartConfig';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import { productCountInCart } from '@jane/bloom/utils/cart/productCountInCart';

/** Sum of counts from in-flight add-to-cart submissions that succeeded. */
const sumPendingAddCounts = (fetchers: Fetcher<ActionResult>[]): number =>
  fetchers
    .filter(({ data, state }) => state === 'loading' && data?.success)
    .map(({ formData }) => Number(formData?.get('count') ?? 1))
    .reduce((prev, acc) => prev + acc, 0);

/**
 * @returns the total size of the bag, or the number of products matching the productId
 */
export const useOptimisticBagSize = (productId?: number): number => {
  const cartItemCount = useActualBagSize(productId);
  const addToCartFetchers = useAddToCartFetchers(productId);

  return cartItemCount + sumPendingAddCounts(addToCartFetchers);
};

export const useActualBagSize = (productId?: number): number => {
  const { cart, pinnedStore } = useRootLoaderData();
  return productCountInCart({
    cart,
    currentStoreId: pinnedStore.id,
    productId,
  });
};

/**
 * Returns a getter for any product's optimistic in-bag count. Use when the set
 * of products is dynamic (e.g. the compare modal) and per-product hooks aren't
 * an option. Recomputes as the cart and add-to-cart fetchers change.
 */
export const useOptimisticBagSizeGetter = (): ((
  productId: number
) => number) => {
  const { cart, pinnedStore } = useRootLoaderData();
  const fetchers = useAddToCartFetchers();

  return useCallback(
    (productId: number) =>
      productCountInCart({
        cart,
        currentStoreId: pinnedStore.id,
        productId,
      }) +
      sumPendingAddCounts(
        fetchers.filter(({ key }) => isAddToCartFetcherFor(key, productId))
      ),
    [cart, pinnedStore.id, fetchers]
  );
};
